import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import type { Query } from '@tanstack/react-query';
import { StrictMode, useMemo, type ReactElement, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { createPortal } from 'react-dom';
import { DesktopGlobalSearchMount } from './DesktopGlobalSearch';
import { GlobalSearchModalMount } from './GlobalSearchModal';
import { MaybeReactQueryDevtools } from './MaybeReactQueryDevtools';
import { ModelSearchMount } from './ModelSearch';

/** 20 min: datos “frescos” sin refetch automático por stale. */
const RNM_STALE_MS = 20 * 60 * 1000;
/** Tiempo que una query inactiva permanece en memoria antes del GC. */
const RNM_GC_MS = 25 * 60 * 1000;
/** Máxima antigüedad del snapshot persistido al rehidratar (sessionStorage). */
const RNM_PERSIST_MAX_AGE_MS = 25 * 60 * 1000;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: RNM_STALE_MS,
      gcTime: RNM_GC_MS,
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

function shouldDehydrateRnmQuery(query: Query): boolean {
  const k = query.queryKey;
  return Array.isArray(k) && k[0] === 'rnm';
}

function createPersistOptions():
  | {
      persister: ReturnType<typeof createSyncStoragePersister>;
      maxAge: number;
      buster: string;
      dehydrateOptions: { shouldDehydrateQuery: typeof shouldDehydrateRnmQuery };
    }
  | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const persister = createSyncStoragePersister({
      storage: window.sessionStorage,
      key: 'rnm_tanstack_rq_v1',
      throttleTime: 1000,
    });
    return {
      persister,
      maxAge: RNM_PERSIST_MAX_AGE_MS,
      buster: String(window.rnmAjax?.queryPersistBuster ?? '0'),
      dehydrateOptions: {
        shouldDehydrateQuery: shouldDehydrateRnmQuery,
      },
    };
  } catch {
    return null;
  }
}

function RnmQueryProvider({ children }: { children: ReactNode }): ReactElement {
  const persistOptions = useMemo(() => createPersistOptions(), []);

  if (persistOptions) {
    return (
      <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
        {children}
      </PersistQueryClientProvider>
    );
  }

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}

function SearchPortals(): ReactElement {
  const elModel = document.getElementById('rnm-model-react-mount');
  const elGlobal = document.getElementById('rnm-global-react-mount');
  const elDesk = document.getElementById('rnm-desktop-react-mount');

  return (
    <>
      {elModel ? createPortal(<ModelSearchMount />, elModel) : null}
      {elGlobal ? createPortal(<GlobalSearchModalMount />, elGlobal) : null}
      {elDesk ? createPortal(<DesktopGlobalSearchMount />, elDesk) : null}
    </>
  );
}

function mountRnmSearchApp(): void {
  const host = document.getElementById('rnm-menu-react-query-host');
  if (!host) {
    console.warn('Rafas Menu: contenedor React (#rnm-menu-react-query-host) no encontrado');
    return;
  }

  createRoot(host).render(
    <StrictMode>
      <RnmQueryProvider>
        <SearchPortals />
        <MaybeReactQueryDevtools />
      </RnmQueryProvider>
    </StrictMode>
  );
}

function boot(): void {
  mountRnmSearchApp();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}